import { get } from "lodash";
import API_CONSTRUCTOR from "../models/api-model";
import PropertyAPIs from "../models/property";

const apiModel = API_CONSTRUCTOR();
const propertyApi = new PropertyAPIs(apiModel);

const NEARBY_MARKER_MILES = 3;

/** @returns {number} distance in miles */
export const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const getAddressComponent = (components, type) => {
  const c = components.find((x) => x.types && x.types.includes(type));
  return c?.long_name || c?.short_name || "";
};

const getShortState = (components) => {
  const c = components.find(
    (x) => x.types && x.types.includes("administrative_area_level_1")
  );
  return c?.short_name || c?.long_name || "";
};

/**
 * Turn map center coords into a /v1/search `target` object.
 * @param {{ lat: number, lng: number }} coords
 * @param {Array<object>} mapProperties - plotted search results (with lat/lng)
 * @returns {Promise<object>} target for PropertySearch / getProperties
 */
export async function resolveMapSearchTarget(coords, mapProperties = []) {
  const { lat, lng } = coords;
  if (lat == null || lng == null) {
    throw new Error("no_resolution");
  }

  let nearest = null;
  let nearestDist = Infinity;
  for (const p of mapProperties) {
    if (!p?.latitude || !p?.longitude) continue;
    const d = haversineMiles(lat, lng, p.latitude, p.longitude);
    if (d < nearestDist) {
      nearestDist = d;
      nearest = p;
    }
  }

  if (nearest && nearestDist <= NEARBY_MARKER_MILES) {
    const zip = nearest.zip != null ? String(nearest.zip).trim() : "";
    if (zip) {
      return { zip, label: `Map area near ZIP ${zip}` };
    }
    const county = nearest.county || nearest.countyName;
    const state = nearest.state;
    if (county && state) {
      return {
        county,
        state,
        label: `Map area near ${county}, ${state}`,
      };
    }
  }

  if (!window.google?.maps?.Geocoder) {
    throw new Error("no_geocoder");
  }

  const geocoder = new window.google.maps.Geocoder();
  const results = await new Promise((resolve, reject) => {
    geocoder.geocode({ location: { lat, lng } }, (res, status) => {
      if (status === "OK" && res?.length) resolve(res);
      else reject(new Error("geocode_failed"));
    });
  });

  const components = results[0].address_components || [];
  const postal = getAddressComponent(components, "postal_code");
  if (postal) {
    return { zip: postal, label: `Map area · ZIP ${postal}` };
  }

  const locality =
    getAddressComponent(components, "locality") ||
    getAddressComponent(components, "sublocality") ||
    getAddressComponent(components, "neighborhood");
  const admin2 = getAddressComponent(components, "administrative_area_level_2");
  const shortState = getShortState(components);

  const placeQuery =
    locality && shortState
      ? `${locality}, ${shortState}`
      : admin2 && shortState
        ? `${admin2}, ${shortState}`
        : null;

  if (!placeQuery) {
    throw new Error("no_resolution");
  }

  const sugRes = await propertyApi.getSuggestions(
    encodeURIComponent(placeQuery)
  );
  const list = get(sugRes, "suggestions", []) || [];
  const areaSugg = list.find((s) => s && !s.propertyId);
  if (!areaSugg) {
    throw new Error("no_resolution");
  }
  return {
    ...areaSugg,
    label: areaSugg.label || placeQuery,
  };
}
